/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import * as $ from 'jquery';
import { forkJoin, Observable, BehaviorSubject, Subscription, Subject } from 'rxjs';
import { filter, first, shareReplay, take, takeUntil } from 'rxjs/operators';
import { Injectable, OnDestroy, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  AppSettingsService,
  DEVICE_OS_FAMILY,
  FeatureToggleService,
  GlobalEventBusService,
  GroupContextService,
  hd,
  TicketContextService,
} from '@nx-fe/services';
import { IDeviceViewHandler, TransferOperation } from '../../remote-control-ng/mcrc-api';
import { BroadcastService } from 'app/utils/broadcastService';
import { GlobalEvents } from 'app/core/global-event-constants';
import { DeviceCaptureActivityTypes } from 'app/modules/ticket/ticket-page.service';
import { LegacyRemoteControlConnection, NgRemoteControlConnection } from './remote-control.connection';
import { DeviceLogRequestStatus, RcLogsService } from './rc-logs.service';
import { VideoRecordingStatus } from './video-recording.service';
import { XsI18nPipe } from '@nx-fe/pipes';
import { IWorkPanelWindow } from './work-panel.service';
import { WorkPanelManagerService, WorkPanelStatus, WorkPanelType } from './work-panel-manager.service';
import { SnapshotService } from 'app/modules/ticket/services/snapshot.service';
import { DeviceCheckinStateService } from 'app/modules/signalr/broadcasters/checkin.state.service';
import { RcDeviceService } from './rc-device-service';
import { RemoteControlUserPreferencesService } from '../remote-control-view/remote-control-user-preferences/remote-control-user-preferences.service';
import { RemoteControlTabService } from './rc-tab.service';
import { TaskManagerService } from './task-manager.service';
import { ZoomControllerService } from './zoom-controller.service';
import { FileManagerService } from './file-manager.service';
import { NavigationEnd, Router } from '@angular/router';
import { FileInfo } from 'app/modules/ticket/ticket.model';
import { IRcDeviceContext, RemoteControlStreamingContext } from '../../remote-control-ng/context/deviceContext';
import { CrossWindowMessage, IInitAuthSession, ReturnCode } from '../../remote-control-ng/definitions';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { PerformanceService } from '../../remote-control-ng/services/performanceService';
import { AdminCommandTypes, AdminMessage, GetSysInfoMessage } from '../../remote-control-ng/messages/pocket-com';
import { VideoRecordingCancelMessage } from '../../remote-control-ng/messages/xsight';
import { CrossWindowSocketProxy, CrossWindowEvents, CrossWindowProxy } from '../../remote-control-ng/crossWindowProxy';
import { RCTransferService } from 'app/modules/remote-control-ng/services/rcTransferService';
import { DeviceStateRegistryService, RecordingState, RemoteControlDeviceState } from './device-state-registry.service';
import { IAgentVideoRecordingSettings, IRCCanvasConfig, IRemoteControlTransferData, IScreenCaptureConfig, IRemoteControlServiceTransferable, IVideoRecordingUserConfig } from './remote-control.interfaces';

type IDeviceModel = hd.IDeviceModel;

export interface ImageDataPlaceholder {
  buffer: ArrayBuffer;
  width: number;
  height: number;
}

interface ModelSpecs {
  manufacturer: string;
  models: Array<string>;
  versions: Array<string>;
  multi_screens: boolean;
  streaming: boolean;
  max_scale: number;
}


class RemoteControlContextPopoutStack {
  constructor() { }

  private stack: { context: RemoteControlStreamingContext; viewHandler: IDeviceViewHandler }[] = [];

  public push = (context: RemoteControlStreamingContext, rc: RcContext): RemoteControlStreamingContext => {
    // save the context properties that will be updated by the popout window, which is connection subsciptions and deviceViewHandler, add more if needed
    const savedContext = {
      context,
      viewHandler: context.deviceViewHandler,
    };
    this.stack.push(savedContext);

    // continue using the context and connection
    (context.remoteControlConnection as NgRemoteControlConnection).setupForPopout(rc);
    return context;
  };

  public pop = (): RemoteControlStreamingContext => {
    const savedContext = this.stack.pop();
    if (!savedContext) {
      return null;
    }

    // get the saved context and restore the changed properties
    const context = savedContext.context;
    context.deviceViewHandler = savedContext.viewHandler;
    (context.remoteControlConnection as NgRemoteControlConnection).restoreFromPopout();
    return context;
  };

  public get length(): number {
    return this.stack.length;
  }
}

export interface RemoteControlEventArgs {
  deviceId: string;
  isStreamingRC: boolean;
}

export interface DualScreenConfiguredEventArgs {
  deviceId: string;
  shouldSupportDualScreen: boolean;
}

@Injectable()
export class RemoteControlService implements IRemoteControlServiceTransferable, OnDestroy {
  private rcSessionTransferDeviceId: string = null;
  private stateChangeStartUnbind: Function;
  private destroyUnbind: Function;
  private rcReady = false;
  private isRcContextReady = false;
  private contextReady$: Subscription;
  private unbindRcCanvasReady: Function;
  private recordedVideoNames: string[] = [];
  private newDeviceCaptureActivityCallback: Function;
  private videoDownloadReadyCallback: Function;
  private newDeviceCaptureActivities: hd.IDeviceCaptureActivity[] = [];
  private addNewDeviceCaptureActivityCallback: Function;
  private newVideoAttachedCallback: Function;
  private disablePopoutRequests: { [key: string]: boolean } = {};
  // This is a copy of canvas before we embed standalone RC into ticket. It should be used to replace the new canvas object initilized after embed into ticket page
  // since all the canvas data has to continue display
  private rcCanvasBeforeEmbed: HTMLElement;

  // This is a copy of canvas after we embed standalone RC into ticket. It should be used to set the canvas back to new one after newly embeded RC session is closed.
  // Since HD-7629 the old canvas will keep use the data binding from destroied controllers, so we have to dump the old canvas after embeded RC session is over
  private rcCanvasAfterEmbed: HTMLElement;

  // The flag to see if we need to reset the canvas to new canvas initialized after embed into ticket.
  // Should be set to FALSE once the embeded RC session is closed.
  private resetToCanvasAfterEmbedNeeded = false;
  private openPopoutWindowSubscription: Subscription;
  private externalPopoutDisabled = new BehaviorSubject<boolean>(false);
  private disablePopout = false;
  private _remoteControlPopoutData: IRemoteControlTransferData;

  public popoutWindow: IWorkPanelWindow;
  public externalPopoutDisabled$: Observable<boolean>;

  // In case of pop-out mode, hasError property of rcState is always false and hence isRcError will be set to true
  // when RC disconnects. Only use this varible for pop-out mode and not for embdedded/standalone mode.
  private isRcError = false;

  private rcTitleBarHeight = 45;
  private canvasOuterMinHeight = 524; // HD-10756 - This is to support the RC Panel Minimum height for 1366 X 768 Resolution.
  private rcTitleBarWithSotiHeaderHeight = this.rcTitleBarHeight + 56;
  private onDestroy$ = new Subject<void>();
  private fileActionCancelledSubscription: Subscription;

  private _remoteControlStarted: Subject<RemoteControlEventArgs> = new Subject<RemoteControlEventArgs>();
  public RemoteControlStarted = this._remoteControlStarted.asObservable();
  private _remoteControlEmbedFinished: Subject<RemoteControlEventArgs> = new Subject<RemoteControlEventArgs>();
  public RemoteControlEmbedFinished = this._remoteControlEmbedFinished.asObservable();

  // private _remoteControlStreamingStarted: Subject<RemoteControlEventArgs> = new Subject<RemoteControlEventArgs>();
  // RemoteControlStreamingStarted = this._remoteControlStreamingStarted.asObservable();

  private _deviceDualScreenConfigured: Subject<DualScreenConfiguredEventArgs> =
    new Subject<DualScreenConfiguredEventArgs>();
  public deviceDualScreenConfigured: Observable<DualScreenConfiguredEventArgs> =
    this._deviceDualScreenConfigured.asObservable();

  private _videoStreamingSupportedForCurrentDevice: boolean = undefined;
  public get videoStreamingSupportedForCurrentDevice() {
    return this._videoStreamingSupportedForCurrentDevice;
  }

  public set videoStreamingSupportedForCurrentDevice(value: boolean) {
    this._videoStreamingSupportedForCurrentDevice = value;
    this._rcDisplayArea = value
      ? this.document.getElementById('rcScreenContainer')
      : this.document.getElementById('rc_canvas');
  }

  public tranferInProgress = false;

  private rcContext: { [deviceId: string]: RemoteControlStreamingContext } = {};
  private rcContextPopoutStack: RemoteControlContextPopoutStack;

  private $window: IWorkPanelWindow = window as any;
  private document: Document = document;
  private crossWindowProxy: CrossWindowSocketProxy = new CrossWindowSocketProxy(window);

  private static _modelSpecs: ModelSpecs[];

  constructor(
    private http: HttpClient,
    private deviceService: RcDeviceService,
    private saI18nPipe: XsI18nPipe,
    private remoteControlTabService: RemoteControlTabService,
    private appSettingsService: AppSettingsService,
    private featureToggleService: FeatureToggleService,
    private zoomControllerService: ZoomControllerService,
    private taskManagerService: TaskManagerService,
    private remoteControlUserPreferencesService: RemoteControlUserPreferencesService,
    // private $transitions: TransitionService,
    private deviceCheckinState: DeviceCheckinStateService,
    private snapshotService: SnapshotService,
    private globalEventBusService: GlobalEventBusService,
    private ticketContextService: TicketContextService,
    private workPanelManagerService: WorkPanelManagerService,
    private groupContextService: GroupContextService,
    private fileManagerService: FileManagerService,
    private rcLogsService: RcLogsService,
    private router: Router,
    private performance: PerformanceService,
    private transferService: RCTransferService,
    readonly deviceStateRegistry: DeviceStateRegistryService,
    private injector: Injector
  ) {
    // RcContext.setInjector(injector);
    this.document.addEventListener('rcSetVideoRecordingFileName', this.syncRecordedVideoName);
    this.document.addEventListener('rcDeviceCaptureActivityCreated', this.syncNewDeviceCaptureActivity);
    this.document.addEventListener('rcDeviceLogsRequestCompleted', this.onDeviceLogsActivityReady);
    this.document.addEventListener('remoteControlLogin', (e: any) => this.deviceStateRegistry.onSessionLogin(e.deviceId));
    this.document.addEventListener('rcVideoDownloadReady', (e: any) => this.onVideoDownloadReady(e.fileInfo));

    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter(
          route => route instanceof NavigationEnd && this.deviceStateRegistry.anyRcConnected() && !this.isTransferingRemoteControlSession()
        )
      )
      .subscribe((event: NavigationEnd) => {
        console.warn(`Transitioning to another page (${event.url}). Stopping all active sessions`);
        this.stopAllRemoteControls();
      });

    this.deviceStateRegistry.popoutRemoteControlStop.onMessage = this.handlePopoutRemoteControlStopping;
    this.deviceStateRegistry.remoteControlSessionEstablished.onMessage = this.handleRemoteControlSessionEstablished;

    this.externalPopoutDisabled$ = this.externalPopoutDisabled.asObservable();

    this.deviceStateRegistry.deviceSnapshotLockoutStatusChanged.onMessage = (lockoutRequest: hd.DeviceSnapshotRequestStatus) => {
      this.disablePopoutRequests[lockoutRequest.requestName] = lockoutRequest.inProgress;

      let externalDisableRequests = false;
      Object.values(this.disablePopoutRequests).forEach(
        value => (externalDisableRequests = externalDisableRequests || value)
      );

      this.disablePopout = externalDisableRequests;
      this.externalPopoutDisabled.next(this.disablePopout);
    };

    this.deviceStateRegistry.remoteControlErrorBroadcast.onMessage = () => {
      this.isRcError = true;
      if (this.featureToggleService.isEnabled('DeveloperMode')) {
        console.info('RC Socket connection closed with error');
      }

      this.closeRc();
    };
  }

  private closeRc = (): void => {
    RcContext.instance.session.close();
    RcContext.instance.clear();
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();

    if (!this.deviceStateRegistry.isEmpty()) {
      this.unbindCallbacks(false);
    }

    if (this.contextReady$) {
      this.contextReady$.unsubscribe();
      this.contextReady$ = undefined;
    }
    this._deviceDualScreenConfigured.complete();
  }

  public isPoppedOutAllowed = (device: IDeviceModel): boolean =>
    device !== undefined &&
    this.deviceStateRegistry.getState(device.deviceId) !== undefined &&
    !this.isVideoStreaming(device.mcDeviceId);

  public stopRcIncompleteSessions = (): void => {
    this.deviceStateRegistry.filterAndApply(
      (state: RemoteControlDeviceState) => state.hasError || state.loading,
      (state: RemoteControlDeviceState) => {
        this.stopRemoteControl(state.device);
      }
    )
  };

  public syncRecordedVideoName = (): void => {
    if (RcContext.instance.recordedVideoName) {
      const videoNameFullPath = RcContext.instance.recordedVideoName.split('\\');
      const videoName = videoNameFullPath[videoNameFullPath.length - 1];
      this.recordedVideoNames.push(videoName);
      console.log('Start recording - Update video name: ' + this.recordedVideoNames);
      RcContext.instance.recordedVideoName = null;
    }
  };

  public syncNewDeviceCaptureActivity = (): void => {
    if (RcContext.instance.draftDeviceCaptureActivity) {
      const draftDeviceCaptureActivity = RcContext.instance.draftDeviceCaptureActivity as hd.IDeviceCaptureActivity;
      console.log('new captured activity created, starting to attach to the ticket');
      this.getAddNewDeviceCaptureActivityCallback()(draftDeviceCaptureActivity);

      if (this.getNewDeviceCaptureActivityCallback) {
        this.getNewDeviceCaptureActivityCallback()(
          draftDeviceCaptureActivity.captureType === DeviceCaptureActivityTypes.adbLogs,
          draftDeviceCaptureActivity.activityId
        );
      }

      if (
        draftDeviceCaptureActivity.captureType === DeviceCaptureActivityTypes.adbLogs &&
        (this.rcLogsService.getDeviceLogRequestStatus() === DeviceLogRequestStatus.Pending ||
          this.rcLogsService.getDeviceLogRequestStatus() === DeviceLogRequestStatus.Delayed)
      ) {
        this.rcLogsService.setDeviceLogRequestStatus(DeviceLogRequestStatus.Attaching);
      }
    }
  };

  public onRcStreamingStarted = (deviceId: string) => {
    // this._remoteControlStreamingStarted.next({ deviceId: deviceId, isStreamingRC: true });
    // TODO - the device id is in some components and services MC, and somewhere XS.
    // This needs to be cleaned up across RC protocols and components
    let rcState = this.deviceStateRegistry.getStateByMcDeviceId(deviceId);
    if (rcState) {
      rcState.running = true;
    }
  };

  public onVideoDownloadReady = (fileInfo: FileInfo): void => {
    if (RcContext.instance.videoActivityId) {
      if (this.featureToggleService.isEnabled('DeveloperMode')) {
        console.log('new video activity created');
      }

      if (this.getVideoDownloadReadyCallback) {
        this.getVideoDownloadReadyCallback()(RcContext.instance.videoActivityId, fileInfo);
      }
    }
  };

  public onDeviceLogsActivityReady = (): void => {
    if (RcContext.instance.draftDeviceCaptureActivity && this.getNewDeviceCaptureActivityCallback) {
      this.getNewDeviceCaptureActivityCallback()(false, RcContext.instance.draftDeviceCaptureActivity.activityId);
    }
  };

  public addNewDeviceCaptureActivity = (draftDeviceCaptureActivity: hd.IDeviceCaptureActivity): void => {
    if (this.newDeviceCaptureActivities.indexOf(draftDeviceCaptureActivity) === -1) {
      this.newDeviceCaptureActivities.push(draftDeviceCaptureActivity);
    }
  };

  public removeNewDeviceCaptureActivity = (draftDeviceCaptureActivity: hd.IDeviceCaptureActivity): void => {
    this.newDeviceCaptureActivities = this.newDeviceCaptureActivities.filter(
      activity => activity.activityId !== draftDeviceCaptureActivity.activityId
    );
  };

  public clearNewDeviceCaptureActivities = (): void => {
    this.newDeviceCaptureActivities = [];
  };

  public getNewDeviceCaptureActivities = (): hd.IDeviceCaptureActivity[] => this.newDeviceCaptureActivities;

  public addNewRecordedVideoName = (name: string): void => {
    if (this.recordedVideoNames.indexOf(name) === -1) {
      this.recordedVideoNames.push(name);
    }
  };

  public removeRecordedVideoName = (name: string): void => {
    this.recordedVideoNames = this.recordedVideoNames.filter(videoNames => videoNames !== name);
  };

  public getRecordedVideoNames = (): string[] => this.recordedVideoNames;

  /**
   * Called to transfer state from the given RemoteControlService to this one, for the specified device.
   * This method is called during RC popout to initialize the RC service in the popout window to the
   * existing state for the specified connected device.
   */
  public transferDeviceState = (
    deviceState: RemoteControlDeviceState,
    fromService: RemoteControlService
  ): Promise<RemoteControlDeviceState> =>
    new Promise<RemoteControlDeviceState>((resolve, reject) => {
      // initialize the rc context for popout window
      const fromServiceImpl = fromService as RemoteControlService;
      const mcDeviceId = deviceState.device.mcDeviceId;
      let context: RemoteControlStreamingContext = null;
      context = fromServiceImpl.rcContext[mcDeviceId];
      // for video streaming, initialize context and prepare connection for the new window
      if (context && context.videoStreaming) {
        fromServiceImpl.rcContextPopoutStack = new RemoteControlContextPopoutStack();
        this.rcContext[mcDeviceId] = fromServiceImpl.rcContextPopoutStack.push(context, RcContext.instance); // just reuse the existing context (from old window) by saving some properties that might be updated by popout window
      }

      const device = deviceState.device;
      this.setupRemoteControl(device).then(
        () => {
          this.videoStreamingSupportedForCurrentDevice = this.isVideoStreaming(device.mcDeviceId);
        },
        (err: hd.IError) => {
          this.deviceStateRegistry.onError(device.deviceId, err.error_description);
          this.closeRc();
          reject(err);
        }
      );

      this.waitForRcCanvas().then(() => {
        if (context && context.videoStreaming) {
          context.deviceViewHandler.prepare();
        }

        const deviceId = deviceState.device.deviceId;

        // Note: Storing a direct reference to complex types below results in both RC service instances pointing to the same objects
        this.deviceStateRegistry.copyFrom(fromServiceImpl.deviceStateRegistry, deviceId, deviceState);
        // Subscribe to neccessary callbacks
        RcContext.instance.session.getConnection().subscribeCloseHandler(this.webSocketCloseHandler);
        this.document.addEventListener('rcSessionEstablished', this.rcSessionEstablishedCallback);

        this.verifyDualScreenDevice(device).then((supportsDualScreen: boolean) => {
          const monitorId = RcContext.instance.multiScreen.requestMonitorId;
          if (supportsDualScreen && monitorId > 0) {
            const monitor = RcContext.instance.multiScreen.monitorList.find(m => m.id === monitorId);
            if (monitor) {
              RcContext.instance.multiScreen.updateMonitorInfo(monitor);
            }
          }
        });

        // XTODO
        // this.stateChangeStartUnbind = this.$transitions.onSuccess({}, this.pageChange);

        this.document.addEventListener('click', this.handleInputsByFocus);

        resolve(deviceState);
      });
    });

  public setDeviceLogRequestStatus = (status: DeviceLogRequestStatus, message: string = null): void => {
    this.rcLogsService.setDeviceLogRequestStatus(status, message);
  };

  public createVideoDownloadUri = (videoActivityId: number, filename: string): Promise<string> => {
    console.debug('Posting to create download for video attachment');
    const request = {
      attachmentActivityId: videoActivityId,
      filename,
    };
    const requestObservable = this.http.post<string>(
      this.appSettingsService.remoteControlServiceUrl + 'rcsessions/createDownload',
      request,
      {}
    );
    return new Promise<string>((resolve, reject) => {
      requestObservable.pipe(first()).subscribe(resolve, reject);
    });
  };

  public embedIntoTicket = (deviceState: RemoteControlDeviceState) => {
    this.startRemoteControlTransfer(deviceState.device.deviceId);
    // XTODO:
    // const fileManagerState = this.fileManagerService.getState(deviceState.device.mcDeviceId || deviceState.device.deviceId);
    // if (fileManagerState) {
    //     fileManagerState.updateScrollPos();
    // }
  };

  public receiveRemoteControlTransfer = (deviceState: RemoteControlDeviceState): Promise<any> => {
    const promise = new Promise<any>((resolve, reject) => {
      if (deviceState.connected) {
        this.verifyDualScreenDevice(deviceState.device);
        this.transferCanvasBuffer(this.$window).then(() => {
          this.transferRemoteControlData();
          resolve(null);
        });
      } else {
        resolve(null);
      }
    });
    return promise;
  };

  /** Builds IRemoteControlTransferData to be transfered */
  public createRemoteControlTransferData = (deviceState: RemoteControlDeviceState): IRemoteControlTransferData =>
  ({
    // checkin state is needed to initialize popout window with current state
    checkinStatus: this.deviceCheckinState.checkinStatus,
    remoteControlService: this,
    snapshotServiceState: this.snapshotService.getSnapshotServiceState(),
    remoteControlTabService: this.remoteControlTabService,
    fileManagerService: this.fileManagerService,
    taskManagerService: this.taskManagerService,
    zoomControllerService: this.zoomControllerService,
    rcContext: RcContext.instance,
    deviceState,
    allowableActions: this.ticketContextService.allowableActions,
    currentGroupId: this.groupContextService.currentGroupId,
    canvasImageData: {
      buffer: null,
      width: 0,
      height: 0,
    },
    broadcastSessionId: BroadcastService.sessionId,
    onClose: () => { },
  } as any);

  public setCanvasOuterHeight = () => {
    const canvasOuter: JQuery = !this.isVideoStreaming() ? $('#canvasOuter') : null;
    if (canvasOuter) {
      let header = 0;
      if (this.deviceStateRegistry.getCurrentState()?.poppedOut) {
        // when popout
        header = this.rcTitleBarHeight;
      } else {
        // in ticket page and standalone
        header = this.rcTitleBarWithSotiHeaderHeight;
      }

      const rcview: JQuery = $('#rc-view');
      this.setVerticalScrollBar(rcview, canvasOuter, header);
    }
  };

  public setErrorPageHeight = () => {
    const errorContent: JQuery = $('.error-screen-content');
    const errorContainer: JQuery = $('.error-screen-container');
    if (errorContent) {
      let header = 0;
      if (this.workPanelManagerService.isPanelTypePoppedOut(WorkPanelType.rc)) {
        // when popout
        header = this.rcTitleBarHeight;
      } else {
        // in ticket page and standalone
        header = this.rcTitleBarWithSotiHeaderHeight;
      }
      this.setVerticalScrollBar(errorContainer, errorContent, header);
    }
  };

  public get remoteControlPopoutData(): IRemoteControlTransferData {
    return this._remoteControlPopoutData;
  }

  public popOutValidate = (device: IDeviceModel): boolean => {
    if (!device) {
      return false;
    }

    if (!this.deviceStateRegistry.canPopout(device) || !this.disablePopout) {
      return !this.disablePopout;
    }

    const state = this.deviceStateRegistry.getState(device.deviceId);
    if (!state || state.poppingOut) {
      return false;
    }

    return true;
  };

  public popoutOnExistingWindow = (device: IDeviceModel, popoutWindow: IWorkPanelWindow): void => {
    // XTODO:
    const state = this.deviceStateRegistry.getState(device.deviceId);
    state.poppingOut = true;

    this.remoteControlTransferSrc$.next();

    if (!state.poppedOut) {
      // Initialize popout data that will be shared with the new window
      this._remoteControlPopoutData = {
        checkinStatus: this.deviceCheckinState.checkinStatus,
        snapshotServiceState: this.snapshotService.getSnapshotServiceState(),
        remoteControlService: this,
        remoteControlTabService: this.remoteControlTabService,
        fileManagerService: this.fileManagerService,
        taskManagerService: this.taskManagerService,
        zoomControllerService: this.zoomControllerService,
        rcContext: RcContext.instance,
        deviceState: state,
        allowableActions: this.ticketContextService.allowableActions,
        currentGroupId: this.groupContextService.currentGroupId,
        canvasImageData: {
          buffer: null,
          width: 0,
          height: 0,
        },
        broadcastSessionId: BroadcastService.sessionId,
        onClose: () => {
          popoutWindow = null;
          this.workPanelManagerService.removeActiveWorkPanelsByStatus(WorkPanelStatus.poppedOut);
          // Stop the RC session (only if state is still popped out or popping out)
          if (state.poppingOut || state.poppedOut) {
            state.popoutWindow = null;

            if (this.deviceStateRegistry.isRemoteControlActive(device)) {
              this.stopRemoteControl(device);
            }
          } else {
            // Fire popout closed callbacks
            this.deviceStateRegistry.onSessionPopout(false);
          }
        },
      };

      this.transfer(this.remoteControlPopoutData.remoteControlService);

      // Store scroll position before old file explorer is destroyed
      const fileManagerState = this.fileManagerService.getState(device.deviceId || device.mcDeviceId);
      if (fileManagerState) {
        fileManagerState.updateScrollPos();
      }

      // Ensure session is not minimized when it pops out
      state.minimized = false;

      // onbeforeunload - the earliest event to stop an uninitialized RC session.
      popoutWindow.onbeforeunload = () => {
        if (this.remoteControlPopoutData && this.remoteControlPopoutData.onClose) {
          this.remoteControlPopoutData.onClose();
        }
      };

      // Indicates that our child window is ready to receive its popout data. This is neccessary since setting the
      // popout data too early on the window can result in a race condition, where it can be lost once the window loads
      popoutWindow.workPanelTransferData.remoteControlTransferData = this.remoteControlPopoutData;
      this.transferCanvasBuffer(popoutWindow).then(() => {
        this.completePopOut(state, popoutWindow);
      });
    }
  };

  public popoutOnWindow = (device: IDeviceModel, createPopoutWindowFunc: () => any): void => {
    const state = this.deviceStateRegistry.getState(device.deviceId);
    state.poppingOut = true;

    // Start popout function once panel is expanded
    this.openPopoutWindowSubscription = this.globalEventBusService.register(
      GlobalEvents.remoteControlOpenPopoutWindow,
      () => {
        if (this.openPopoutWindowSubscription) {
          this.openPopoutWindowSubscription.unsubscribe();
        }
        this.remoteControlTransferSrc$.next();

        if (state.connected && !state.poppedOut) {
          let popoutWindow: IWorkPanelWindow = null;

          // If the user navigates away from the current page, close the popout window
          this.$window.addEventListener('unload', function (e) {
            if (popoutWindow && !popoutWindow.closed) {
              popoutWindow.close();
              popoutWindow = null;
            }
          });

          const unbindPopoutMessageHandler = this.crossWindowProxy.onMessageForwarded((msg: CrossWindowMessage) => {
            this.rcPopoutMessageHandler(msg, popoutWindow, state);
          });
          // console.debug('Registered onWindowMessage rcPopoutMessageHandler');

          // Initialize popout data that will be shared with the new window
          this._remoteControlPopoutData = {
            checkinStatus: this.deviceCheckinState.checkinStatus,
            snapshotServiceState: this.snapshotService.getSnapshotServiceState(),
            remoteControlService: this,
            remoteControlTabService: this.remoteControlTabService,
            fileManagerService: this.fileManagerService,
            taskManagerService: this.taskManagerService,
            zoomControllerService: this.zoomControllerService,
            rcContext: RcContext.instance,
            deviceState: state,
            allowableActions: this.ticketContextService.allowableActions,
            currentGroupId: this.groupContextService.currentGroupId,
            canvasImageData: {
              buffer: null,
              width: 0,
              height: 0,
            },
            broadcastSessionId: BroadcastService.sessionId,
            onClose: () => {
              popoutWindow = null;
              this.workPanelManagerService.removeActiveWorkPanelsByStatus(WorkPanelStatus.poppedOut);
              // Remove window message handler
              if (unbindPopoutMessageHandler) {
                unbindPopoutMessageHandler();
              }

              // Stop the RC session (only if state is still popped out or popping out)
              if (state.poppingOut || state.poppedOut) {
                state.popoutWindow = null;

                // When popup is opened and we start remote control from device card then "stopRemoteControl" method
                // is already called and hence we don't need to call it again from here and that is why we are checking
                // "_remoteControlPopoutData" which would be null in that case. [HD-12744]
                if (this.deviceStateRegistry.isRemoteControlActive(device) && this._remoteControlPopoutData) {
                  this.stopRemoteControl(device);
                }
              } else {
                // Fire popout closed callbacks
                this.deviceStateRegistry.onSessionPopout(false);
              }
            },
          };

          this.transfer(this.remoteControlPopoutData.remoteControlService);

          // Store scroll position before old file explorer is destroyed
          const fileManagerState = this.fileManagerService.getState(device.deviceId || device.mcDeviceId);
          if (fileManagerState) {
            fileManagerState.updateScrollPos();
          }

          // Ensure session is not minimized when it pops out
          state.minimized = false;

          popoutWindow = createPopoutWindowFunc();
          // onbeforeunload - the earliest event to stop an uninitialized RC session.
          popoutWindow.onbeforeunload = () => {
            if (this.remoteControlPopoutData && this.remoteControlPopoutData.onClose) {
              this.remoteControlPopoutData.onClose();
            }
          };

          popoutWindow.focus();
        }
      }
    );

    // Fire popout open callbacks
    this.deviceStateRegistry.onSessionPopout(true);
  };

  // Register to receive messages on our main window from the popout window
  private rcPopoutMessageHandler = (msg: CrossWindowMessage, popoutWindow: IWorkPanelWindow, state: RemoteControlDeviceState) => {
    // console.debug(`Receiving postMessage in rcPopoutMessageHandler ${msg?.type} on ${this.crossWindowProxy.window?.location.href}`, msg);

    switch (msg?.type) {
      case CrossWindowEvents.PR_POPOUT_READY:
        // Indicates that our child window is ready to receive its popout data. This is neccessary since setting the
        // popout data too early on the window can result in a race condition, where it can be lost once the window loads
        if (!popoutWindow.workPanelTransferData) {
          (<any>popoutWindow).workPanelTransferData = {};
        }
        popoutWindow.workPanelTransferData.remoteControlTransferData = this.remoteControlPopoutData;
        this.transferCanvasBuffer(popoutWindow).then(() => {
          this.completePopOut(state, popoutWindow);
        });
        break;
      case CrossWindowEvents.WORK_PANEL_POPOUT_READY:
        this.transferCanvasBuffer(popoutWindow).then(() => {
          this.completePopOut(state, popoutWindow);
        });
        break;
      case CrossWindowEvents.ATTACH_SCREENSHOT:
        // A screenshot was sent from the popout window
        if (msg.data.fileObject) {
          // The filename on the file Blob seems to be lost when the message is sent across windows,
          // so restore it using the filename in the main message
          msg.data.fileObject.name = msg.data.name;
        }
        this.globalEventBusService.broadcast(GlobalEvents.attachScreenshot, msg.data);
        break;
      case CrossWindowEvents.RC_POPIN:
        this.completePopin(popoutWindow);
        break;
    }
  };



  /** Should be called in the context of the rc popout window, NOT the main SA window */
  public popin = (deviceState: RemoteControlDeviceState, mainWindow: IWorkPanelWindow) => {
    if (!this.deviceStateRegistry.canPopout(deviceState.device)) {
      return;
    }

    if (this.$window !== deviceState.popoutWindow) {
      console.error('Calling RC popin from non-popout window');
      return;
    }

    if (!mainWindow || mainWindow === this.$window) {
      console.error('Invalid main window argument provided to RC popin');
      return;
    }

    this.unsubscribePopoutCloseMonitoring(deviceState.popoutWindow);
    deviceState.poppedOut = false;
    deviceState.popoutWindow = null;

    // Store scroll position before old file explorer is destroyed
    const fileManagerState = this.fileManagerService.getState(
      deviceState.device.mcDeviceId || deviceState.device.deviceId
    );
    if (fileManagerState) {
      fileManagerState.updateScrollPos();
    }

    this.remoteControlTransferSrc$.next();
    // Set RC transfer data on the parent window;
    if (!mainWindow.workPanelTransferData) {
      mainWindow.workPanelTransferData = {} as any;
    }

    mainWindow.workPanelTransferData.remoteControlTransferData = {
      snapshotServiceState: this.snapshotService.getSnapshotServiceState(),
      remoteControlService: this,
      remoteControlTabService: this.remoteControlTabService,
      fileManagerService: this.fileManagerService,
      taskManagerService: this.taskManagerService,
      zoomControllerService: this.zoomControllerService,
      checkinStatus: this.deviceCheckinState.checkinStatus,
      currentGroupId: null,
      rcContext: RcContext.instance,
      deviceState,
      allowableActions: [],
      canvasImageData: {
        buffer: null,
        width: 0,
        height: 0,
      },
      broadcastSessionId: BroadcastService.sessionId,
      onClose: () => { },
    };

    this.transferCanvasBuffer(mainWindow).then(() => {
      // Send a message to the main window telling it to complete the popin
      CrossWindowProxy.postMessage(mainWindow, CrossWindowEvents.RC_POPIN);
    });
  };

  public notifyNewVideoAvailable = () => {
    const state = this.deviceStateRegistry.getCurrentState();
    if (state && state.recordingPopoverOpen !== undefined) {
      state.recordingPopoverOpen = false;
    }

    this.getNewVideoAvailableCallback()();
    //XTODO:
    if (state && state.popoutWindow) {
      CrossWindowProxy.postMessage(state.popoutWindow, CrossWindowEvents.NEW_VIDEO_ATTACHED);
    }
  };

  public transfer = (remoteControlService: IRemoteControlServiceTransferable) => {
    this.newDeviceCaptureActivityCallback = remoteControlService.getNewDeviceCaptureActivityCallback();
    this.addNewDeviceCaptureActivityCallback = remoteControlService.getAddNewDeviceCaptureActivityCallback();
    this.deviceStateRegistry.transfer(remoteControlService.deviceStateRegistry);
  };

  /**
   * Handles remote control transfer from another window to this window
   */
  public transferRemoteControlData = (transferOperation: TransferOperation = TransferOperation.transfer) => {
    let transferData: IRemoteControlTransferData = null;
    if (this.$window.workPanelTransferData && this.$window.workPanelTransferData.remoteControlTransferData) {
      transferData = this.$window.workPanelTransferData.remoteControlTransferData;
    }

    if (transferData) {
      // tranferInProgress so don't reload data use data from remote transfer instead.
      this.tranferInProgress = true;
      const deviceId = transferData.deviceState.device.deviceId;
      this.deviceCheckinState.checkinStatus = transferData.checkinStatus;
      this.snapshotService.setSnapshotServiceState(transferData.snapshotServiceState);

      if (transferData.currentGroupId) {
        this.groupContextService.currentGroupId = transferData.currentGroupId;
      }
      // Transfer RC socket and connection state only if connection is successful
      if (!transferData.deviceState.hasError) {
        RcContext.instance.transfer(transferData.rcContext, this.featureToggleService, transferOperation);
      } else {
        // set "mcrc.sotiAssistDeviceId" to deviceId because it doesn't get set in case of RC failure.
        RcContext.instance.sotiAssistDeviceId = deviceId;
      }

      this.transfer(transferData.remoteControlService);

      // Transfer tab state (ensure this happens last)
      if (this.isRcError || transferData.deviceState.hasError) {
        this.isRcError = false;
      } else {
        // If we are starting RC from an existing pop up window, then the latest state (if any)
        // Is already in here (the pop up window), so shouldn't need to override it with what is comming from main window
        // XTODO:
        if (transferOperation === TransferOperation.transfer) {
          this.remoteControlTabService.transfer(deviceId, transferData.remoteControlTabService);
        }

        // // Set callbacks for when user saves settings
        this.remoteControlUserPreferencesService.setOnUpdateVideoRecordingPrefs(this.updateVideoRecordingPrefs);
        this.remoteControlUserPreferencesService.setOnUpdateScreenCapturePrefs(this.updateScreenCapturePrefs);
        this.remoteControlUserPreferencesService.setOnUpdateRCCanvasPrefs(this.updateRCCanvasPrefs);
        this.remoteControlUserPreferencesService.setOnUpdateDeviceLogTypePrefs(this.updateDeviceLogPrefs);
        this.remoteControlUserPreferencesService.setOnUpdateAgentVideoRecordingPrefs(
          this.updateAgentVideoRecordingPrefs
        );

        // Transfer zoom and file explorer states
        this.zoomControllerService.transfer(deviceId, transferData.zoomControllerService);
        this.fileManagerService.transfer(transferData.fileManagerService, deviceId);

        this.taskManagerService.transfer(transferData.taskManagerService, deviceId);

        // for video streaming, fire 'rcSessionEstablished' event for the new window.
        // PC_RESTART will be sent when rcCanvas is resized by setCanvasOuterHeight() <= zoomController.doResize() <= zoomController.onRemoteControlTabSelectedSub()
        const state = this.rcContext[transferData.deviceState.device.mcDeviceId];
        if (state && state.videoStreaming) {
          (state.remoteControlConnection as NgRemoteControlConnection).onRcSessionEstablished(this.document); // fire 'rcSessionEstablished' event
        }

        if (
          RcContext.instance.security.RemoteControlViewSystemInfo &&
          this.isLegacyDeviceInfoAvailable(transferData.deviceState.device.family)
        ) {
          // Update status for DeviceInfo
          RcContext.instance.session.sendRequest(new GetSysInfoMessage());
        }
      }

      if (transferOperation === TransferOperation.start) {
        //XTODO:
        this.remoteControlUserPreferencesService.setRCCanvasParameters(transferData.deviceState.rcCanvasConfigPrefs);
      }
      this.tranferInProgress = false;
      this.globalEventBusService.broadcast(GlobalEvents.remoteControlCompleteTransfer, true);
      this.transferService.completeTransfer();
    }
  };


  public confirmStartRC = (): boolean => {
    if (this.deviceStateRegistry.remoteControlActiveCount() > 0) {
      const state = this.deviceStateRegistry.first();
      if (!state) {
        return true;
      }

      let messageString = 'ticket_EndRemoteControl_Message';

      if (this.canCancelVideoRecording(state)) {
        messageString =
          state.recordingState === RecordingState.UploadingVideo
            ? 'admin_Incident_Video_Processing_Message'
            : 'ticket_Cancel_VideoRecording_Message';
      }

      if (this.isFileUploadOrDownloadInProgress()) {
        messageString = 'ticket_Cancel_File_Upload_Download_Message';
      }

      if (!this.$window.confirm(this.saI18nPipe.transform(messageString))) {
        return false;
      }

      // If we got here we are still cancelling after the prompt, so if there is a video, send the cancel message
      if (this.canCancelVideoRecording(state)) {
        // cancel the video recording
        RcContext.instance.session.sendRequest(new VideoRecordingCancelMessage());
        state.recordingState = RecordingState.NotRunning;
        this.deviceStateRegistry.videoRecordingStatusChanged.postMessage(<VideoRecordingStatus>{
          state: RecordingState.NotRunning,
          withAudio: false,
        });
      }

      if (this.isFileUploadOrDownloadInProgress()) {
        /** Cancel action for all the pending files */
        this.cancelFileAction();
      }
    }
    return true;
  };

  /** when the user stop RC without properly ending the file upload and download, the current action will be cancelled */
  private cancelFileAction = () => {
    this.fileManagerService.cancelEntireFileAction();
  };

  private _rcDisplayArea: any;

  private getRCDisplayArea = () => {
    return this._rcDisplayArea;
  };

  /**
   * Starts an RC session transfer for the given device ID.
   */
  public startRemoteControlTransfer = (deviceId: string): void => {
    this.rcSessionTransferDeviceId = deviceId;
    this.rcCanvasBeforeEmbed = this.getRCDisplayArea();
    // this.rcCanvasBeforeEmbed.style.display = 'none';
  };

  /**
   * Attempts to complete a pending RC session transfer.
   *
   * @return Device ID of the completed session transfer, or null if no session transfer was in progress.
   */
  public endRemoteControlTransfer = (): string => {
    if (this.rcSessionTransferDeviceId) {
      const deviceId = this.rcSessionTransferDeviceId;
      this._rcDisplayArea = this.isVideoStreamingSupportedForCurrentDevice()
        ? this.document.getElementById('rcScreenContainer')
        : this.document.getElementById('rc_canvas');
      this.rcCanvasAfterEmbed = this.getRCDisplayArea();

      if (!!this.rcCanvasAfterEmbed) {
        const viewParentElement = this.rcCanvasAfterEmbed.parentElement;
        viewParentElement.removeChild(this.rcCanvasAfterEmbed);
        viewParentElement.appendChild(this.rcCanvasBeforeEmbed);
        this.rcCanvasBeforeEmbed.style.display = 'block';
        this.rcSessionTransferDeviceId = null;
        this.resetToCanvasAfterEmbedNeeded = true;
        this._remoteControlEmbedFinished.next({
          deviceId,
          isStreamingRC: this.videoStreamingSupportedForCurrentDevice,
        });
        // this._remoteControlStreamingStarted.next({ deviceId: deviceId, isStreamingRC: this.videoStreamingSupportedForCurrentDevice });
        return deviceId;
      }
    }
    return null;
  };

  /**
   * Reset current canvas to the canvas after embedded
   */
  public resetCanvasToAfterEmbed = () => {
    if (
      !this.videoStreamingSupportedForCurrentDevice &&
      this.resetToCanvasAfterEmbedNeeded &&
      this.rcCanvasAfterEmbed
    ) {
      const oldView = this.document.getElementById(this.rcCanvasAfterEmbed.id);
      if (oldView) {
        const viewParentElement = oldView.parentElement;
        viewParentElement.removeChild(oldView);
        viewParentElement.appendChild(this.rcCanvasAfterEmbed);
        this.rcCanvasAfterEmbed.style.display = 'block';
        this.resetToCanvasAfterEmbedNeeded = false;
      }
    }
  };

  private _remoteControlCanvasReady = new Subject();

  public setRcCanvasReady = () => {
    this.rcReady = true;
    this._remoteControlCanvasReady.next(null);
    // this.globalEventBusService.broadcast(GlobalEvents.remoteControlCanvasReady, null);
    // console.log('event:rcCanvasReady');
  };

  /**
   * Initiates remote control of the given device.
   *
   * @param device The device to
   * @param startPoppedOut Starts and RC session and transfers it to an already existing PopOut
   */
  public startRemoteControl = (
    device: IDeviceModel,
    ticketId: string,
    startPoppedOut: boolean = false
  ): Promise<IDeviceModel> =>
    // TODO NT: Replacing the promise with async/await will make debugging, understanding and maintaining this code a lot easier
    new Promise<IDeviceModel>((resolve, reject) => {
      // Ensure we reload the device log settings and cached categories from a posible previous RC session
      this.rcLogsService.resetPrefs();
      console.log('startRemoteControl: ', device.deviceId);
      this.rcReady = false; // ensure waitForRcCanvas() will wait for 'rcCanvasReady' event

      // The 'rcCanvasReady' event is emitted by UI components, which are initialized according to
      // *ngIf="isVideoStreamingSupported()" in rc-view-v2.component.html
      // isVideoStreamingSupported() will have value only after setupRemoteControl() is finished.
      // In case of retry (Connect Again), as this.rcReady is true, this.waitForRcCanvas() won't wait.
      Promise.all([this.setupRemoteControl(device), this.waitForRcCanvas()]).then(
        () => {
          this.stopRcIncompleteSessions();
          if (this.deviceStateRegistry.remoteControlActiveCount() > 0) {
            // TODO: This code needs to be removed once multiple devices per page is introduced
            this.stopAllRemoteControls().then(() => {
              this.startRemoteControlSafely(device, ticketId, startPoppedOut, resolve, reject);
              // orphaned RC sessions (e.g. disconnected mid session) do not get closed until
              // they are processed from the stopRcIncompleteSessions above. Closing these
              // sessions late means that they could also reset the canvas state in the rc-view-v2.component.html
              // This could happen for a fresh session, the canvas that has just been turned on in the startRemoteControl
              // call preceding this startRemoteControl. To avoid a race condition, make sure that
              // the canvas and streaming flag are set correctly now that everything is done
              this.videoStreamingSupportedForCurrentDevice = this.isVideoStreaming(device.mcDeviceId);
              this._remoteControlStarted.next({
                deviceId: this.getDeviceId(device),
                isStreamingRC: this.videoStreamingSupportedForCurrentDevice,
              });
            });
          } else {
            this.startRemoteControlSafely(device, ticketId, startPoppedOut, resolve, reject);
          }
        },
        (err: hd.IError) => {
          this.deviceStateRegistry.onError(device.deviceId, err.error_description);
          this.closeRc();

          // Transfer session to the pop out window.
          // If we are starting a Remote Control session on behalf of a popout (startPoppedOut=true)
          if (startPoppedOut) {
            this.popoutOnExistingWindow(device, this.popoutWindow);
          }

          reject(err);
        }
      );
    });

  public handleInputsByFocus = (event: any) => {
    const deviceInput = RcContext.instance.deviceInput;
    if (deviceInput) {
      let element = null;
      if (!this.isVideoStreamingSupportedForCurrentDevice()) {
        element = this.getRCDisplayArea();
      } else {
        element = this.document.getElementById('rcScreenContainer');
      }
      if (event.target.id === 'rcVideo' && !this.deviceStateRegistry.isRemoteViewOnly(this.deviceStateRegistry.getCurrentState().device)) {
        deviceInput.registerKeyInputListeners();
      } else if (event.target.id === 'rcInputCanvas' && !this.deviceStateRegistry.isRemoteViewOnly(this.deviceStateRegistry.getCurrentState().device)) {
        deviceInput.registerKeyInputListeners();
      }
      // isEqualNode that was originally used to check if the focus is on the RC display area
      // was too heavy handed, not allowing any changes between the 2, resulting in unregistering
      // listeners after the RC tramsfer from standalone. Comparing the id, type and classes should be enough
      else if (
        !!element?.id &&
        event.target.id === element.id &&
        event.target.nodeType === element.nodeType &&
        event.target.classList.length === element.classList.length &&
        [...event.target.classList].every(x => element.classList.contains(x)) &&
        !element.classList.contains('no-input')
      ) {
        deviceInput.registerKeyInputListeners();
      } else {
        deviceInput.unregisterKeyInputListeners();
      }
    }
  };

  public stopRemoteControl = (device: IDeviceModel, shouldCancelVideoRecording: boolean = true): boolean => {
    const isConnected = RcContext.instance.session.isConnected();
    const state = this.deviceStateRegistry.getState(device.deviceId);
    const isFileUploadOrDownloadInProgress = this.isFileUploadOrDownloadInProgress();

    if (this.canCancelVideoRecording(state) && shouldCancelVideoRecording && !state.poppedOut && !state.hasError) {
      // when the user stop RC without properly ending the recording, the recording will be cancelled
      RcContext.instance.session
        .sendRequest(new VideoRecordingCancelMessage())
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.closeSession(device);
        });
    } else if (isFileUploadOrDownloadInProgress) {
      if (this.fileActionCancelledSubscription) {
        this.fileActionCancelledSubscription.unsubscribe();
      }

      // when the user stop RC without properly ending file download or upload.
      //XTODO:
      this.fileActionCancelledSubscription = this.fileManagerService.fileActionCancelled.subscribe(() => {
        this.closeSession(device);
        if (this.remoteControlTabService) {
          this.remoteControlTabService.resetState(null, device.deviceId);
        }
      });

      this.cancelFileAction();
    } else {
      this.closeSession(device);
    }
    this.deviceStateRegistry.resetVideoRecordingState(state);
    this.unbindCallbacks(!isConnected);

    if (state && !isFileUploadOrDownloadInProgress) {
      this.fileManagerService.resetState(device.deviceId);
      this.deviceStateRegistry.resetState(device.deviceId);
    }

    this.resetCanvasToAfterEmbed();
    this.zoomControllerService.resetState(device.deviceId);

    return true;
  };

  // Used to notify Assist new UI of server-side video recording attachment
  public setNewVideoAvailableCallback = (callback: Function) => {
    if (this.newVideoAttachedCallback === undefined) {
      this.newVideoAttachedCallback = null;
    }
    this.newVideoAttachedCallback = callback;
  };

  // Used to notify Assist new UI of server-side video recording attachment
  public getNewVideoAvailableCallback = () => {
    if (this.newVideoAttachedCallback) {
      return this.newVideoAttachedCallback;
    } else {
      return () => { };
    }
  };

  public getAddNewDeviceCaptureActivityCallback = (): Function => {
    if (this.addNewDeviceCaptureActivityCallback) {
      return this.addNewDeviceCaptureActivityCallback;
    } else {
      return () => false;
    }
  };

  public setAddNewDeviceCaptureActivityCallback = (callback: Function): void => {
    if (this.addNewDeviceCaptureActivityCallback === undefined) {
      this.addNewDeviceCaptureActivityCallback = null;
    }
    this.addNewDeviceCaptureActivityCallback = callback;
  };

  public getNewDeviceCaptureActivityCallback = (): Function => {
    if (this.newDeviceCaptureActivityCallback) {
      return this.newDeviceCaptureActivityCallback;
    } else {
      return () => false;
    }
  };

  public setNewDeviceCaptureActivityCallback = (callback: Function): void => {
    if (this.newDeviceCaptureActivityCallback === undefined) {
      this.newDeviceCaptureActivityCallback = null;
    }
    this.newDeviceCaptureActivityCallback = callback;
  };

  public getVideoDownloadReadyCallback = (): Function => {
    if (this.videoDownloadReadyCallback) {
      return this.videoDownloadReadyCallback;
    } else {
      return () => false;
    }
  };

  public setVideoDownloadReadyCallback = (callback: Function): void => {
    if (this.videoDownloadReadyCallback === undefined) {
      this.videoDownloadReadyCallback = null;
    }
    this.videoDownloadReadyCallback = callback;
  };

  /**
   * Returns the start time of the current RC session for the given device ID.
   */
  public getRcStartTime = (deviceId: string): Date =>
    this.deviceStateRegistry.getState(deviceId)?.startTime || null;

  /**
   * Updates the ticket ID for a current RC session for the given device Id.
   */
  public updateTicketId = (deviceId: string, ticketId: number): void => {
    if (this.deviceStateRegistry.getState(deviceId)?.connected) {
      RcContext.instance.ticketId = ticketId.toString();
      RcContext.instance.session.sendMessage(
        new AdminMessage({
          commandType: AdminCommandTypes.helpDeskSetTicketId,
          ticketId: RcContext.instance.ticketId,
        })
      );
    }
  };

  /**
   * Updates the user preferences for video recording.
   */
  public updateVideoRecordingPrefs = (newValue: IVideoRecordingUserConfig): void => {
    const state = this.deviceStateRegistry.getCurrentState();
    if (state) {
      const prefs = state.videoRecordingPrefs;
      if (prefs) {
        prefs.recordMouseMovement = newValue.recordMouseMovement;
        prefs.recordMouseClicks = newValue.recordMouseClicks;
        prefs.includeDeviceSkin = newValue.includeDeviceSkin;
      }
    }
  };

  /**
   * Updates the user preferences for agent video recording.
   */
  public updateAgentVideoRecordingPrefs = (newValue: IAgentVideoRecordingSettings): void => {
    const state = this.deviceStateRegistry.getCurrentState();
    if (state) {
      state.agentVideoRecordingPrefs = newValue;
      const appUI = RcContext.instance.session.appUI;
      appUI.setAgentRecordingScreenWidth(state.agentVideoRecordingPrefs.screenWidth);
      appUI.setAgentRecordingScreenHeight(state.agentVideoRecordingPrefs.screenHeight);
      appUI.setAgentRecordingVideoFrameRate(state.agentVideoRecordingPrefs.videoFrameRate);
      appUI.setAgentRecordingVideoBitRate(state.agentVideoRecordingPrefs.videoBitRate);
      appUI.setAgentRecordingAudioSamplingRate(state.agentVideoRecordingPrefs.audioSamplingRate);
      appUI.setAgentRecordingAudioBitRate(state.agentVideoRecordingPrefs.audioBitRate);
    }
  };

  /**
   * Updates the user preferences for screen capture.
   */
  public updateScreenCapturePrefs = (newValue: IScreenCaptureConfig): void => {
    const state = this.deviceStateRegistry.getCurrentState();
    if (state) {
      const prefs = state.screenCapturePrefs;
      if (prefs) {
        prefs.imageFormat = newValue.imageFormat;
        prefs.imageQuality = newValue.imageQuality;
        prefs.bitDepth = newValue.bitDepth;
      }
    }
  };

  /**
   * Updates the user preferences for RC screen canvas.
   */
  public updateRCCanvasPrefs = (newValue: IRCCanvasConfig): void => {
    const state = this.deviceStateRegistry.getCurrentState();
    if (state) {
      const prefs = state.rcCanvasConfigPrefs;
      if (prefs) {
        prefs.colourQuality = newValue.colourQuality;
        prefs.imageQuality = newValue.imageQuality;
      }
    }
  };

  /**
   * Updates the user preferences for RC Device Log Dump type.
   */
  public updateDeviceLogPrefs = (/* newValue: IDeviceLogConfig */): void => {
    // const state = this.deviceStateRegistry.getCurrentState();
    // if (state) {
    //     const prefs = state.deviceLogConfigPrefs;
    //     if (prefs) {
    //         prefs.reportType = newValue.reportType;
    //         prefs.dumpLogsCategories = newValue.dumpLogsCategories;
    //     }
    // }
  };

  public isRCContextReady = (): boolean => this.isRcContextReady;

  private waitForRcCanvas = (): Promise<void> =>
    new Promise<void>(resolve => {
      if (this.rcReady) {
        resolve();
      } else {
        this._remoteControlCanvasReady.pipe(take(1)).subscribe(() => {
          resolve();
        });
      }
    });

  private stopAllRemoteControls = (): Promise<{}> =>
    new Promise<{}>(resolve => {
      this.globalEventBusService.register(GlobalEvents.remoteControlSafeToStart, () => {
        resolve({});
      });

      //
      // Because of *ngIf="(supportVideoStreaming | async) in remote-control-view.componemt.html, supportVideoStreaming is resolved only when this.startRemoteControl() is called,
      // and this.waitForRcCanvas() will block until 'rcCanvasReady' is fired.
      // In such case, RemoteControlActivityController.$onInit() (triggered by TickController.addActivity()) will be called before 'rcCanvasReady' is fired,
      // therefore a new deviceState entry is added(by calling getOrCreateState() from RemoteControlActivityController.$onInit()), before stopAllRemoteControls() is called.
      // The old RC doesn't have this problem because this.rcReady is true therefore waitForRcCanvas() returns immediately, and RemoteControlActivityController.$onInit() will be executed after startRemoteControl().
      //
      let stopping = false;
      this.deviceStateRegistry.filterAndApply(
        (state: RemoteControlDeviceState) => state.active && !!state.device,
        (state: RemoteControlDeviceState) => {
          stopping = true;
          this.stopRemoteControl(state.device);
        }
      )
      if (!stopping) {
        this.globalEventBusService.broadcast(GlobalEvents.remoteControlSafeToStart, null);
      }
    });

  private startRemoteControlSafely = (
    device: IDeviceModel,
    ticketId: string,
    startPoppedOut: boolean,
    resolve: (value: hd.IDeviceModel | PromiseLike<hd.IDeviceModel>) => void,
    reject: (reason?: any) => void
  ) => {
    if (!this.deviceStateRegistry.canSupportRC(device)) {
      console.debug('This device cannot be remote controlled:', device);

      // Construct an error message that can be displayed to the user
      let errorMsg: string;
      if (!device.supportsRC) {
        errorMsg = this.saI18nPipe.transform('remoteControl_Device_NoRCSupport_Message');
      } else if (!device.isAgentOnline) {
        errorMsg = this.saI18nPipe.transform('remoteControl_Device_Offline_Message');
      } else {
        errorMsg = this.saI18nPipe.transform('remoteControl_Device_CantRC_Message');
      }
      reject({ error_description: errorMsg } as hd.IError);
      return;
    }

    const context = this.rcContext[this.getDeviceId(device)];
    const alreadyConnected = context?.remoteControlConnection?.isConnected();

    if (!alreadyConnected) {
      const state = this.deviceStateRegistry.getOrCreateState(device.deviceId);
      state.device = device;
      state.loading = true;
      state.connected = false;
      state.running = false;
      state.active = true;
      state.hasError = false;
      state.poppedOut = false;

      this.initializeUserPreferences(state).subscribe(() => {
        RcContext.instance.ticketId = ticketId;
        RcContext.instance.session.getConnection().subscribeCloseHandler(this.webSocketCloseHandler);
        this.document.addEventListener('rcSessionEstablished', this.rcSessionEstablishedCallback);

        this.remoteControlEnded$.pipe(takeUntil(this.onDestroy$), take(1)).subscribe((vDeviceId: string) => {
          this.rcSessionEnded(vDeviceId);
        });

        this.rcLogsService.setDeviceLogRequestStatus(DeviceLogRequestStatus.None);

        // setup preferences like color depth, only work for legacy RC.
        // for video streaming the preferences can be picture quality.
        if (this.remoteControlUserPreferencesService) {
          this.remoteControlUserPreferencesService.initSession(state.rcCanvasConfigPrefs);
        }

        // TODO - either fix video streaming to support startPoppedOut, or clean up the unused code if startPoppedOut is not supported any more.
        if (context.videoStreaming && !!context.deviceViewHandler) {
          context.deviceViewHandler.prepare();
        }

        context.remoteControlConnection.connect(startPoppedOut);

        resolve(device);

        RcContext.instance.multiScreen.requestMonitorId = 0;

        // If we are starting a Remote Control session on behalf of a popout (startPoppedOut=true)
        // Then we should now transfer the session to the pop out window
        if (startPoppedOut) {
          this.popoutOnExistingWindow(device, this.popoutWindow);
        }

        // XTODO:
        //this.stateChangeStartUnbind = this.$transitions.onSuccess({}, this.pageChange);
        this.document.addEventListener('click', this.handleInputsByFocus);
      });
    } else {
      reject({ error_description: this.saI18nPipe.transform('remoteControl_AlreadyInUse_Message') } as hd.IError);
    }
  };

  private initializeUserPreferences = (state: RemoteControlDeviceState): Observable<any> => {
    const service = this.remoteControlUserPreferencesService;

    // Use safe default values
    const videoRecordingDefaults: IVideoRecordingUserConfig = {
      recordMouseMovement: service.defaultRecordMouseMovement(),
      recordMouseClicks: service.defaultRecordMouseClicks(),
      includeDeviceSkin: service.defaultIncludeDeviceSkin(),
    };
    const screenCaptureDefaults: IScreenCaptureConfig = {
      imageFormat: service.defaultImageFormat(),
      imageQuality: service.defaultImageQuality(),
      bitDepth: service.defaultBitDepth(),
    };
    const rcCanvasConfigDefaults: IRCCanvasConfig = {
      colourQuality: service.defaultRCColourQuality(),
      imageQuality: service.defaultRCImageQuality(),
    };
    // const deviceLogConfigDefaults: IDeviceLogConfig = {
    //   reportType: service.defaultDeviceLogReportType(),
    //   dumpLogsCategories: service.defaultDeviceLogCategory(),
    // };

    state.videoRecordingPrefs = videoRecordingDefaults;
    state.screenCapturePrefs = screenCaptureDefaults;
    state.rcCanvasConfigPrefs = rcCanvasConfigDefaults;
    //state.deviceLogConfigPrefs = deviceLogConfigDefaults;

    state.agentVideoRecordingPrefs = this.remoteControlUserPreferencesService.defaultAgentVideoRecordingPrefs(
      <IAgentVideoRecordingSettings>{}
    );

    // Update device state based on actual values from database
    const getVideoRecordingUserConfigObs = service.getVideoRecordingUserConfig().pipe(shareReplay(1));
    getVideoRecordingUserConfigObs.subscribe(result => {
      state.videoRecordingPrefs.recordMouseMovement = service.defaultRecordMouseMovement(result.recordMouseMovement);
      state.videoRecordingPrefs.recordMouseClicks = service.defaultRecordMouseClicks(result.recordMouseClicks);
      state.videoRecordingPrefs.includeDeviceSkin = service.defaultIncludeDeviceSkin(result.includeDeviceSkin);
    });
    const getScreenCaptureConfigObs = service.getScreenCaptureConfig().pipe(shareReplay(1));
    getScreenCaptureConfigObs.subscribe(result => {
      state.screenCapturePrefs.imageFormat = service.defaultImageFormat(result.imageFormat);
      state.screenCapturePrefs.imageQuality = service.defaultImageQuality(result.imageQuality);
      state.screenCapturePrefs.bitDepth = service.defaultBitDepth(result.bitDepth);
    });
    const getRCCanvasConfigObs = service.getRCCanvasConfig().pipe(shareReplay(1));
    getRCCanvasConfigObs.subscribe(result => {
      state.rcCanvasConfigPrefs.colourQuality = service.defaultRCColourQuality(result.colourQuality);
      state.rcCanvasConfigPrefs.imageQuality = service.defaultRCImageQuality(result.imageQuality);
    });
    const getDeviceConfigObs = service.getDeviceLogTypeConfig().pipe(shareReplay(1));
    getDeviceConfigObs.subscribe(result => {
      // state.deviceLogConfigPrefs.reportType = service.defaultDeviceLogReportType(result.reportType);
    });
    const resultObs = forkJoin([
      getVideoRecordingUserConfigObs,
      getScreenCaptureConfigObs,
      getRCCanvasConfigObs,
      getDeviceConfigObs,
    ]);

    // Set callbacks for when user saves settings
    service.setOnUpdateVideoRecordingPrefs(this.updateVideoRecordingPrefs);
    service.setOnUpdateScreenCapturePrefs(this.updateScreenCapturePrefs);
    service.setOnUpdateRCCanvasPrefs(this.updateRCCanvasPrefs);
    service.setOnUpdateDeviceLogTypePrefs(this.updateDeviceLogPrefs);
    service.setOnUpdateAgentVideoRecordingPrefs(this.updateAgentVideoRecordingPrefs);

    return resultObs;
  };

  private closeSession = (device: IDeviceModel): void => {
    this.closeRc();

    this.deviceStateRegistry.resetVideoRecordingState();
    this.videoStreamingSupportedForCurrentDevice = undefined;
    this._remoteControlStarted.next({ deviceId: undefined, isStreamingRC: undefined });

    if (device) {
      const state = this.deviceStateRegistry.getState(device.deviceId);
      if (state) {
        state.loading = false;
        state.connected = false;
        state.running = false;

        this.remoteControlEndedSrc$.next(device.deviceId);
        this.globalEventBusService.broadcast(GlobalEvents.remoteControlEnded, device.deviceId);

        // When RC in PopOut $rootScope messaging doesn't work.
        // We have to send an additional message between windows using the BroadcastService.
        if (state.poppedOut) {
          this.deviceStateRegistry.remoteControlEndedMessageForPopOut.postMessage(device.deviceId);
        }

        if (this.remoteControlTabService && !state.hasError) {
          this.remoteControlTabService.resetState(null, device.deviceId);
        }
      }

      // cleanup popout stack
      if (this.rcContextPopoutStack) {
        this.rcContext[device.mcDeviceId] = this.rcContextPopoutStack.pop();
        this.rcContextPopoutStack = null;
      }

      // close video streaming
      const context = this.rcContext[device.mcDeviceId];
      if (context) {
        // TODO - remove the entry? for now keep the entry for quick reuse
        context.remoteControlSharedData = null;
        if (context.deviceViewHandler) {
          context.deviceViewHandler.cleanup();
        }

        if (context.remoteControlConnection) {
          context.remoteControlConnection.disconnect();
          context.remoteControlConnection = null;
        }
      }
    }

    if (this.remoteControlPopoutData) {
      this._remoteControlPopoutData = null;
    }

    if (this.contextReady$) {
      this.contextReady$.unsubscribe();
      this.contextReady$ = undefined;
      this.isRcContextReady = false;
    }
  };

  private canCancelVideoRecording = (state: RemoteControlDeviceState): boolean => {
    if (!state) {
      return false;
    }

    return (
      state.recordingState !== RecordingState.NotRunning && state.recordingState !== RecordingState.TranscodingVideo
    );
  };

  private isFileUploadOrDownloadInProgress = (): boolean => this.fileManagerService.isCommandInProgress;

  private unbindCallbacks = (signalEnding: boolean): void => {
    // Unbind all the events we have bound
    if (RcContext.instance.deviceInput) {
      RcContext.instance.deviceInput.unregisterKeyInputListeners();
    }

    if (this.stateChangeStartUnbind) {
      this.stateChangeStartUnbind();
      this.stateChangeStartUnbind = undefined;
    }
    if (this.destroyUnbind) {
      this.destroyUnbind();
      this.destroyUnbind = undefined;
    }

    // // Clear callbacks for when user saves settings
    if (this.remoteControlUserPreferencesService) {
      this.remoteControlUserPreferencesService.setOnUpdateVideoRecordingPrefs(null);
      this.remoteControlUserPreferencesService.setOnUpdateScreenCapturePrefs(null);
      this.remoteControlUserPreferencesService.setOnUpdateRCCanvasPrefs(null);
      this.remoteControlUserPreferencesService.setOnUpdateDeviceLogTypePrefs(null);
      this.remoteControlUserPreferencesService.setOnUpdateAgentVideoRecordingPrefs(null);
    }

    // Notify the preferences service that the session has ended
    if (this.remoteControlUserPreferencesService) {
      this.remoteControlUserPreferencesService.endSession();
    }

    // Notify the filemanager service that the RC session has ended
    if (this.fileManagerService) {
      this.fileManagerService.endSession();
      this.fileManagerService.clearStatusCenterData();
      this.fileManagerService.isFieldIndicatorActive = false;
      this.fileManagerService.isCommandInProgress = false;
    }

    if (signalEnding) {
      this.globalEventBusService.broadcast(GlobalEvents.remoteControlSafeToStart, null);
    }
  };

  private webSocketCloseHandler = (event): void => {
    let message = '';
    let messageCaption = '';
    let messageDescription = '';
    const returnCode = RcContext.instance.session.getConnection().getLastError();

    if (returnCode === ReturnCode.PCR_ALREADY_CONNECTED) {
      message = this.saI18nPipe.transform('remoteControl_AlreadyInUse_Message');
    } else if (returnCode === ReturnCode.PCR_LOGIN_REJECTED) {
      message = this.saI18nPipe.transform('remoteControl_UserDeclined_Message');
    } else if (returnCode === ReturnCode.PCR_TIMEOUT) {
      message = this.saI18nPipe.transform('remoteControl_Timeout_Message');
    } else if (returnCode === ReturnCode.PCR_USER_LOGGED_OUT) {
      message = this.saI18nPipe.transform('remoteControl_User_Logged_Out_Message');
      messageCaption = this.saI18nPipe.transform('remoteControl_User_Logged_Out_Message_Caption');
      messageDescription = this.saI18nPipe.transform('remoteControl_User_Logged_Out_Message_Description');
    } else if (event.code >= 1000) {
      message = this.saI18nPipe.transform('remoteControl_WebSocketCode' + event.code + '_Message');
    }

    if (message) {
      if (this.featureToggleService.isEnabled('DeveloperMode')) {
        console.info('RC Socket connection closed with error');
      }
      this.deviceStateRegistry.onError(event.deviceId, message, messageCaption, messageDescription);
      this.closeRc();
    }

    const handler = RcContext.instance.deviceContextHandler;
    if (handler) {
      handler.updateWithSessionEnd();
    }

    if (this.rcLogsService.getDeviceLogRequestStatus() !== DeviceLogRequestStatus.None) {
      this.rcLogsService.setDeviceLogRequestStatus(DeviceLogRequestStatus.Error);
    }

    // If we were recording a video, we need to clean that state
    const state = this.deviceStateRegistry.first();
    if (state && state.recordingState !== RecordingState.NotRunning) {
      state.recordingState = RecordingState.NotRunning;
      this.deviceStateRegistry.videoRecordingStatusChanged.postMessage(<VideoRecordingStatus>{
        state: RecordingState.NotRunning,
        withAudio: false,
      });
    }

    this.unbindCallbacks(true);
    if (!!event.deviceId) {
      this.globalEventBusService.broadcast(GlobalEvents.remoteControlEnded, event.deviceId);

      //   const context = this.rcContext[event.deviceId];
      // this.closeSession(context?.device);
    }
  };

  private rcSessionEstablishedCallback = (event: any): void => {
    this.deviceStateRegistry.remoteControlSessionEstablished.postMessage(event.deviceId);
    this.rcSessionEstablished(event.deviceId);
    this.document.removeEventListener('rcSessionEstablished', this.rcSessionEstablishedCallback);
  };

  private rcSessionEstablished = (mcDeviceId: string): void => {
    const state = this.deviceStateRegistry.getStateByMcDeviceId(mcDeviceId);
    state.loading = false;
    state.connected = true;
    // this does not change the state.running, it is done from the first full screen
    this.rcSessionStarted(state.device.deviceId);
    this.remoteControlStartedSrc$.next(state.device);
    this.deviceStateRegistry.isCurrentRemoteControlRunning$()
      .pipe(
        filter(isRunning => isRunning === true),
        first()
      )
      .subscribe(isRunning => {
        this.globalEventBusService.broadcast(GlobalEvents.remoteControlStarted, state.device);
      });

    if (!this.rcContext[mcDeviceId] || !this.rcContext[mcDeviceId].videoStreaming) {
      this.contextReady$ = RcContext.instance.deviceContextHandler.contextReady$.subscribe((isReady: boolean) => {
        this.isRcContextReady = isReady;
      });
    } else {
      // the code above uses subscription to ensure device info/first screen/skin/canvas are all ready, before the video recording button can be enabled
      // it will be nice to do same in the NxtRemoteControlScreenService class, but don't see it's necessary. simply set isRcContextReady to true here.
      this.isRcContextReady = true;
    }
  };

  // When we start RC from popOut 'rcSessionEstablished' from mcrc-com-Session only comes to popOut remoteControlService.
  // To start remoteControlActivity correctly we should send a message to the remoteControlService main window.
  private handleRemoteControlSessionEstablished = (mcDeviceId: string): void => {
    const state = this.deviceStateRegistry.getStateByMcDeviceId(mcDeviceId);
    if (state) {
      state.loading = false;
      state.connected = true;
      // this does not change the state.running, it is done from the first full screen

      if (state.device) {
        this.rcSessionStarted(state.device.deviceId);
      }
    }
  };

  private rcSessionStarted = (deviceId: string): void => {
    // Set start time for RC session
    this.deviceStateRegistry.onSessionStarted(deviceId);
    this.performance.rcSessionStarted(deviceId);
  };

  private rcSessionEnded = (deviceId: string): void => {
    this.deviceStateRegistry.onSessionEnded(deviceId);

    if (this.unbindRcCanvasReady) {
      this.unbindRcCanvasReady();
    }

    this.performance.rcSessionEnded(deviceId);
  };

  /** Transfers the canvas buffer from the current window to the rc transfer data in the specified window */
  private transferCanvasBuffer = (toWindow: IWorkPanelWindow): Promise<any> =>
    new Promise<any>(resolve => {
      const ctx = <IRcDeviceContext>RcContext.instance.deviceContext;

      if (!ctx || !ctx.canvasBuffer || !ctx.canvasBuffer.width) {
        resolve({});
      } else {
        // When we started RC in tab different than RC, we dont have a canvas to transfer

        const bufferContext = ctx.canvasBuffer.getContext('2d');
        const imgBuffer = bufferContext.getImageData(0, 0, ctx.canvasBuffer.width, ctx.canvasBuffer.height);

        const unbindRcBufferHandler = CrossWindowProxy.onWindowMessage(toWindow, (msg: CrossWindowMessage) => {
          // console.debug(`Receiving postMessage in transferCanvasBuffer ${msg?.type} on ${toWindow?.location.href}`, msg);
          if (msg.type === CrossWindowEvents.RC_BUFFER) {
            unbindRcBufferHandler();
            toWindow.workPanelTransferData.remoteControlTransferData.canvasImageData.buffer = msg.data;
            resolve({});
          }
        });
        // console.debug('Registered onWindowMessage RC_BUFFER');

        if (toWindow.workPanelTransferData.remoteControlTransferData !== null) {
          toWindow.workPanelTransferData.remoteControlTransferData.canvasImageData.width = ctx.canvasBuffer.width;
          toWindow.workPanelTransferData.remoteControlTransferData.canvasImageData.height = ctx.canvasBuffer.height;
        }

        CrossWindowProxy.postMessage(toWindow, CrossWindowEvents.RC_BUFFER, imgBuffer.data.buffer);
      }
    });

  /** Called in the context of the main window to complete the RC popout transfer */
  private completePopOut = (state: RemoteControlDeviceState, popoutWindow: IWorkPanelWindow) => {
    state.poppedOut = true;
    state.poppingOut = false;

    // We only need this subscription during the poppingOut = true,
    // so we should unsibscribe after the transfer session is completed to avoid the call popoutwindow Close twice.
    popoutWindow.onbeforeunload = null;
    this.subscribePopoutCloseMonitoring(popoutWindow);
    state.popoutWindow = popoutWindow;
  };

  // end the RC session if the popout window is closed
  public subscribePopoutCloseMonitoring = (popoutWindow: IWorkPanelWindow) => {
    popoutWindow.addEventListener('unload', this.onPopoutRcClosed);
  };

  // called when the RC session is transferred back to the main window to allow
  // the popout window to be closed gracefully
  public unsubscribePopoutCloseMonitoring = (popoutWindow: IWorkPanelWindow) => {
    popoutWindow.removeEventListener('unload', this.onPopoutRcClosed);
  };

  private onPopoutRcClosed = () => {
    if (this.remoteControlPopoutData && this.remoteControlPopoutData.onClose) {
      this.remoteControlPopoutData.onClose();
    }
  };

  /** Called in the context of the main window to complete the RC popin transfer */
  public completePopin = (popoutWindow: any) => {
    let device = null;

    if (popoutWindow.workPanelTransferData && popoutWindow.workPanelTransferData.remoteControlTransferData) {
      device = popoutWindow.workPanelTransferData.remoteControlTransferData.deviceState.device;
    }

    // restore context for video streaming
    if (device && this.rcContextPopoutStack) {
      this.rcContext[device.mcDeviceId] = this.rcContextPopoutStack.pop();
      this.rcContextPopoutStack = null;
    }

    this.transferRemoteControlData();

    // Once popin transfer has completed, close the original popout window
    if (popoutWindow) {
      popoutWindow.close();

      // Clear the popout data on our window
      if (this.$window.workPanelTransferData && this.$window.workPanelTransferData.remoteControlTransferData) {
        this.$window.workPanelTransferData.remoteControlTransferData = null;
      }
    }

    if (device) {
      this.deviceStateRegistry.minimizeToggle(device, false);

      const deviceState = this.deviceStateRegistry.getState(device.deviceId);
      if (
        deviceState.poppedOut &&
        !popoutWindow.workPanelTransferData.remoteControlTransferData.deviceState.poppedOut
      ) {
        deviceState.poppedOut = false;
      }
    }
  };

  private setVerticalScrollBar = (outerElement: any, innerElement: any, header: number) => {
    const height = window.innerHeight;
    if (height - header >= this.canvasOuterMinHeight) {
      outerElement.removeClass('add-scrollbar-y');
    } else {
      outerElement.addClass('add-scrollbar-y');
    }
  };

  private isLegacyDeviceInfoAvailable = (deviceFamily): boolean => {
    const result = deviceFamily && deviceFamily.indexOf(DEVICE_OS_FAMILY.AndroidPlus) === -1;
    return result;
  };

  private handlePopoutRemoteControlStopping = (device: IDeviceModel): void => {
    if (device && this.deviceStateRegistry.isRemoteControlActive(device)) {
      this.stopRemoteControl(device);
    }
  };

  public getRemoteControlContext = (deviceId?: string): RemoteControlStreamingContext => {
    if (deviceId) {
      return this.rcContext[deviceId];
    }

    // TODO - simlar to this.getCurrentState(), the parameter of device id should not be empty when remote controlling multiple devices is supported.
    for (const key in this.rcContext) {
      if (this.rcContext[key].remoteControlConnection) {
        return this.rcContext[key];
      }
    }

    return null;
  };

  public isVideoStreamingSupportedForCurrentDevice = (): boolean => {
    // before RC starts or after RC ends this value should be undefined so that neither 'canvasOuter' nor 'videoOuter' is in DOM,
    // after setupRemoteControl() is called, the value is set to true or false according to MS response.
    return this.videoStreamingSupportedForCurrentDevice;
  };

  public isVideoStreaming = (deviceId?: string): boolean => {
    const context = this.getRemoteControlContext(deviceId);
    return context ? context.videoStreaming : false;
  };

  private setupRemoteControl = async (deviceModel: IDeviceModel): Promise<void> => {
    let hadError = false;
    const state = this.deviceStateRegistry.getStateByMcDeviceId(deviceModel.mcDeviceId);
    if (state !== undefined) {
      hadError = state.hasError;
      state.hasError = false;
    }

    const deviceId = this.getDeviceId(deviceModel);
    const context = new RemoteControlStreamingContext(deviceModel);
    this.rcContext[deviceId] = context as RemoteControlStreamingContext;

    if (!hadError && context.remoteControlConnection && context.remoteControlSharedData) {
      this.rcReady = true;
      return; // setup already
    }

    let rcData: hd.IRemoteControlModel;
    try {
      // call IDeviceService.getRemoteControlData() which calls MS API GetRemoteControlSharedDataHtml5()
      rcData = await this.deviceService.getRemoteControlData(deviceModel);
    } catch (error) {
      if (error.status === 500) {
        error.error_description += ' (Please check if device is online)';
      }

      console.error(`getRemoteControlData failed: ${JSON.stringify(error)}`);
      this.deviceStateRegistry.onError(deviceId, error.error_description);
      this.closeRc();
      throw error;
    }

    const deviceSpecs = await this.getModelSpecs(deviceModel);
    const supportsDualScreen = this.supportMultiScreens(deviceModel, deviceSpecs);
    let supportVideoStreaming = rcData.data['SupportVideoStreaming'];
    if (supportVideoStreaming !== true ||
      !this.featureToggleService.isEnabled('RemoteControlMp4Streaming') ||
      supportsDualScreen ||
      deviceSpecs?.some(m => m.streaming === false) === true) {
      supportVideoStreaming = false;
    }

    context.remoteControlSharedData = rcData;
    context.videoStreaming = supportVideoStreaming;
    context.maxScale = deviceSpecs?.find(m => m.max_scale > 0)?.max_scale ?? 100;
    context.remoteControlConnection = supportVideoStreaming
      ? new NgRemoteControlConnection(
        rcData,
        context as RemoteControlStreamingContext,
        this.featureToggleService.isEnabled('StreamViaProxy'),
        this,
        this.deviceStateRegistry,
        this.injector
      )
      : new LegacyRemoteControlConnection(rcData);

    // init mcrc even when supportVideoStreaming is true so that legacy JS code for file explorer still works
    RcContext.instance.featureToggleService = this.featureToggleService;
    RcContext.instance.setResponseData(rcData.data);
    if (supportVideoStreaming) {
      RcContext.instance.session.getConnection().initRecordingService(
        rcData.uri,
        () =>
          new Promise<IInitAuthSession>(resolve => {
            this.deviceService.getRemoteControlData(deviceModel).then((data: hd.IRemoteControlModel) => {
              resolve({ token: data.data['RemoteControlSecurityToken'], sessionId: data.data['SessionId'] });
            });
          })
      );
    }

    // unblock *ngIf="isVideoStreamingSupported()" in rc-view-v2.component.html, and will trigger the 'rcCanvasReady' event
    this.videoStreamingSupportedForCurrentDevice = this.isVideoStreaming(deviceModel.mcDeviceId);
    this._remoteControlStarted.next({
      deviceId: this.getDeviceId(deviceModel),
      isStreamingRC: this.videoStreamingSupportedForCurrentDevice,
    });
  };

  /**
    /* searches the list of devices supporting the dual screen RC and notifies deviceDualScreenConfigured subscribers.
    /* returns a boolean indicating if the device supports multiple screens */
  private verifyDualScreenDevice = async (deviceModel: IDeviceModel): Promise<boolean> => {
    const isWindows = this.isWindowsDevice(deviceModel.family);
    const supportsDualScreen = isWindows || (await this.getModelSpecs(deviceModel))?.some(m => m.multi_screens === true) === true;
    this._deviceDualScreenConfigured.next({
      deviceId: this.getDeviceId(deviceModel),
      shouldSupportDualScreen: supportsDualScreen,
    });
    return Promise.resolve(supportsDualScreen);
  };

  private supportMultiScreens = (deviceModel: IDeviceModel, specs: ModelSpecs[]): boolean => {
    const isWindows = this.isWindowsDevice(deviceModel.family);
    const supportsDualScreen = isWindows || specs?.some(m => m.multi_screens === true) === true;
    this._deviceDualScreenConfigured.next({
      deviceId: this.getDeviceId(deviceModel),
      shouldSupportDualScreen: supportsDualScreen,
    });
    return supportsDualScreen;
  };

  private isWindowsDevice = (deviceFamily: string): boolean => {
    return deviceFamily.indexOf(DEVICE_OS_FAMILY.WindowsDesktop) >= 0 ||
      deviceFamily.indexOf(DEVICE_OS_FAMILY.WindowsModernDesktop) >= 0 ||
      deviceFamily.indexOf(DEVICE_OS_FAMILY.WindowsPhone) >= 0;
  }

  private getModelSpecs = async (deviceModel: IDeviceModel): Promise<Array<ModelSpecs>> => {
    // TODO - consider hardcoding the json for the known device specs, to reduce the size of model-specs.json
    try {
      if (RemoteControlService._modelSpecs === undefined) {
        RemoteControlService._modelSpecs = await this.http.get('assets/model-specs.json').toPromise() as Array<ModelSpecs>;
      }

      return RemoteControlService._modelSpecs?.filter(
        m => m.manufacturer === deviceModel.manufacturer &&
          (m.models?.includes(deviceModel.model) ?? true) &&
          (m.versions?.includes(deviceModel.osVersion) ?? true));
    }
    catch (e) {
      console.error('Error loading model-specs.json: ', e);
      return null;
    }
  }

  private isTransferingRemoteControlSession(): boolean {
    return !!this.$window.workPanelTransferData?.remoteControlTransferData;
  }

  private getDeviceId = (device: IDeviceModel): string => (device.mcDeviceId || device.deviceId);

  // V2 Code and Properties
  // ==================================================================================
  private remoteControlStartedSrc$: Subject<hd.IDeviceModel> = new Subject<hd.IDeviceModel>();
  public remoteControlStarted$: Observable<hd.IDeviceModel> = this.remoteControlStartedSrc$.asObservable();

  private remoteControlTransferSrc$: Subject<void> = new Subject<void>();
  public remoteControlTransfer$: Observable<void> = this.remoteControlTransferSrc$.asObservable();

  private remoteControlEndedSrc$: Subject<string> = new Subject<string>();
  public remoteControlEnded$: Observable<string> = this.remoteControlEndedSrc$.asObservable();
}
